import type { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';
import type { SanityImageSource } from '@sanity/image-url/lib/types/types';
import { useHead, useNuxtApp, useSeoMeta } from 'nuxt/app';
import { computed, ref, type MaybeRef } from 'vue';
import { WITH_SITE_TITLE } from '@pkg/sanity-utils/constants/seo';
import { sanityImageWithAsset } from '@pkg/sanity-utils/sanityImageWithAsset';
import type { DefaultSeo } from '@pkg/sanity-utils/types/seo';

type ImageBuilder = (source: SanityImageSource) => ImageUrlBuilder;

export function useSanityDefaultSeoMeta(defaultSeoMaybeRef: MaybeRef<DefaultSeo>) {
  const { $urlFor } = useNuxtApp();
  const defaultSeo = ref(defaultSeoMaybeRef);

  useHead({
    link: computed(() => generateFavicon(defaultSeo.value, $urlFor)),
  });

  useSeoMeta({
    titleTemplate: computed(() => generateTitleTemplate(defaultSeo.value)),
    title: () => text(defaultSeo.value?.metaTitle) || text(defaultSeo.value?.metaTitleFallback),
    ogTitle: () => '%s',
    description: () => text(defaultSeo.value?.metaDescription) || text(defaultSeo.value?.metaDescriptionFallback),
    ogDescription: () => text(defaultSeo.value?.metaDescription) || text(defaultSeo.value?.metaDescriptionFallback),
    ogImage: () => {
      const image = sanityImageWithAsset(defaultSeo.value?.socialImage);
      if (!image) {
        return undefined;
      }
      return $urlFor(image).width(1200).fit('max').quality(90).url();
    },
  });
}

function text(value?: string | null) {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function generateTitleTemplate(defaultSeo: DefaultSeo) {
  const {
    withSiteTitle,
    titleSeparator = '',
  } = defaultSeo;
  const siteTitle = defaultSeo.siteTitle || defaultSeo.fallbackSiteTitle;

  if (withSiteTitle && siteTitle) {
    if (withSiteTitle === WITH_SITE_TITLE.PREPEND) {
      return `${siteTitle} ${titleSeparator} %s`;
    }
    if (withSiteTitle === WITH_SITE_TITLE.APPEND) {
      return `%s ${titleSeparator} ${siteTitle}`;
    }
  }

  return '%s';
}

function generateFavicon(defaultSeo: DefaultSeo, urlFor: ImageBuilder) {
  const favicon = sanityImageWithAsset(defaultSeo.favicon);

  if (!favicon) {
    return [];
  }

  return [{
    hid: 'icon',
    rel: 'icon',
    type: 'image/x-icon',
    href: urlFor(favicon).width(512).height(512).url(),
  }];
}
